import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteServiceCategory, getServiceCategories } from '../../services/services';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import OverlayLoader from '../../components/overlayLoader';
import { SkeletonRows } from '../../components/skeleton';
import SetServiceCategoryModal from './serviceCategoriesSet';
import { toLocalDateString } from '../../utils/helper';
import ConfirmationModal from '../../components/modals/confirmation';

const columns = ['name', 'createdAt']

const PencilIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
);
const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
);

/* Cycle through accent colours for category tiles */
const TILE_ACCENTS = [
    { bg: 'var(--brand-50)',    border: 'var(--brand-100)',  color: 'var(--brand-ink)' },
    { bg: 'var(--cleaning-bg)', border: '#E4D9FB',           color: 'var(--cleaning)'  },
    { bg: 'var(--delivery-bg)', border: '#FBE3BB',           color: 'var(--delivery)'  },
    { bg: 'var(--done-bg)',     border: 'var(--good-bd)',    color: 'var(--good)'      },
];

function getInitials(name) {
    if (!name) return '?';
    return name.trim().slice(0, 2).toUpperCase();
}

const ServiceCategoriesPage = () => {
    const { t } = useTranslation();

    const [serviceCategoriesModal, setServiceCategoriesModal] = useState()
    const [confirmationModal, setConfirmationModal] = useState(false)

    const [categories, setCategories] = useState([])
    const [serviceCategoryToEdit, setServiceCategoryToEdit] = useState()
    const [serviceCategoryToDelete, setServiceCategoryToDelete] = useState()

    const { data, isFetching } = useQuery('service-categories', getServiceCategories)

    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deleteServiceCategory, {
        onSuccess: onMutateSuccess((result, id) => {
            queryClient.invalidateQueries(['service-categories'])
        }, t('settingsPage.serviceCategoryDeleted'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setCategories(result)
        })
    }, [data])

    const handleShowSetServiceCategoryModal = (serviceCategory) => {
        setServiceCategoryToEdit(serviceCategory)
        setServiceCategoriesModal(true)
    }

    const handleShowConfirmation = (serviceCategory) => {
        setServiceCategoryToDelete(serviceCategory)
        setConfirmationModal(true)
    }

    const handleDeleteServiceCategory = () => {
        mutate(serviceCategoryToDelete.id)
    }

    return (
        <div className="sbr">
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('settingsPage.deleteServiceCategory')}
                message={t('settingsPage.confirmDeleteServiceCategory')}
                confirmAction={handleDeleteServiceCategory}
            />
            <SetServiceCategoryModal display={serviceCategoriesModal} toggle={setServiceCategoriesModal} serviceCategoryId={serviceCategoryToEdit?.id} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('settingsPage.serviceCategories')}</div>
                    <div className="sub">{t('settingsPage.serviceCategoriesDesc')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetServiceCategoryModal()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('settingsPage.addCategory')}
                    </button>
                </div>
            </div>

            {/* Category list */}
            <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '6px 16px', position: 'relative', minHeight: '80px' }}>
                    {categories.length === 0 && isFetching ? (
                        <div style={{ padding: '16px 8px' }}>
                            <SkeletonRows rows={4} widths={['18%', '46%', '24%']} />
                        </div>
                    ) : categories.length === 0 ? (
                        <div className="sbr-empty">
                            <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
                                <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
                            </svg>
                            {t('settingsPage.noCategories')}
                        </div>
                    ) : (
                        categories.map((cat, idx) => {
                            const accent = TILE_ACCENTS[idx % TILE_ACCENTS.length];
                            return (
                                <div key={cat.id ?? idx} className="row-sbr">
                                    <div className="a" style={{ background: accent.bg, border: `1px solid ${accent.border}`, color: accent.color }}>
                                        {getInitials(cat.name)}
                                    </div>
                                    <div className="info">
                                        <div className="nm">{cat.name}</div>
                                        {cat.createdAt && (
                                            <div className="sub">{t('settingsPage.addedDate', { date: toLocalDateString(new Date(cat.createdAt)) })}</div>
                                        )}
                                    </div>
                                    <div className="rt" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                            style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                            onClick={() => handleShowSetServiceCategoryModal(cat)}>
                                            <PencilIcon />
                                        </button>
                                        <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                            style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                            onClick={() => handleShowConfirmation(cat)}>
                                            <TrashIcon />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    {(isFetching && categories.length > 0) && <OverlayLoader />}
                </div>
            </div>
        </div>
    );
};

export default ServiceCategoriesPage;
